import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface User {
  email: string;
  role: 'development' | 'admin';
  permissions: string[];
}

export interface UserConfig {
  email: string;
  password: string;
  role: 'development' | 'admin';
  permissions: string[];
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly USER_CONFIGS: UserConfig[] = [
    {
      email: 'development@travelswitch.com',
      password: 'Info@2025#',
      role: 'development',
      permissions: ['translator']
    },
    {
      email: 'Arfat_khan',
      password: 'Travel@123',
      role: 'admin',
      permissions: ['translator', 'key-manager']
    }
  ];

  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$: Observable<User | null> = this.currentUserSubject.asObservable();

  constructor() {
    // Check if user is already logged in (from localStorage)
    const savedUser = localStorage.getItem('currentUser');
    if (savedUser) {
      try {
        const user = JSON.parse(savedUser);
        // Validate user object and restore permissions if missing
        if (user && user.email && user.role) {
          // If permissions are missing, restore them from config
          if (!user.permissions || !Array.isArray(user.permissions)) {
            const userConfig = this.USER_CONFIGS.find(
              config => config.email.toLowerCase() === user.email.toLowerCase()
            );
            if (userConfig) {
              user.permissions = userConfig.permissions;
              // Update localStorage with corrected user
              localStorage.setItem('currentUser', JSON.stringify(user));
            } else {
              // Invalid user, clear it
              localStorage.removeItem('currentUser');
              return;
            }
          }
          this.currentUserSubject.next(user);
        } else {
          // Invalid user structure, clear it
          localStorage.removeItem('currentUser');
        }
      } catch (e) {
        localStorage.removeItem('currentUser');
      }
    }
  }

  login(email: string, password: string): { success: boolean; message?: string; user?: User } {
    const userConfig = this.USER_CONFIGS.find(
      config => config.email.toLowerCase() === email.toLowerCase() && config.password === password
    );

    if (!userConfig) {
      return {
        success: false,
        message: 'Invalid email or password'
      };
    }

    const user: User = {
      email: userConfig.email,
      role: userConfig.role,
      permissions: userConfig.permissions
    };

    // Save to localStorage
    localStorage.setItem('currentUser', JSON.stringify(user));
    this.currentUserSubject.next(user);

    return {
      success: true,
      user
    };
  }

  logout(): void {
    localStorage.removeItem('currentUser');
    this.currentUserSubject.next(null);
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  isAuthenticated(): boolean {
    return this.currentUserSubject.value !== null;
  }

  hasPermission(permission: string): boolean {
    const user = this.currentUserSubject.value;
    if (!user) return false;
    if (!user.permissions || !Array.isArray(user.permissions)) return false;
    return user.permissions.includes(permission);
  }

  canAccessTranslator(): boolean {
    return this.hasPermission('translator');
  }

  canAccessKeyManager(): boolean {
    return this.hasPermission('key-manager');
  }
}

